import { Bullet, Circle, ICircleSettings, Label, Template, color, p50, percent } from "@amcharts/amcharts5";
import {
  AxisRendererX,
  AxisRendererY,
  ILineSeriesSettings,
  LineSeries,
  ValueAxis,
  XYChart,
  XYCursor,
  XYSeries,
} from "@amcharts/amcharts5/xy";
import { cloneDeep, isNil, map, omitBy, orderBy } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { mapToRange } from "@/helpers/charts";
import Lang from "@/lang";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { chartFontSettings, chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";
import { ContentWidget } from "@/types/layouts";

interface MinMax {
  [key: string]: number | undefined;
}

type BubbleSeries = { name: string; data?: LineSeries };

const minMaxDefault = () => ({ xAxis: undefined, yAxis: undefined, displayMetric: undefined });

const DOT_SIZE_STEP = 3;
const LABELS_INDICATOR_PADDING = 22;
const EXTRA_X_SPACE = 0.03;
const EXTRA_Y_MIN = 0.15;
const EXTRA_Y_MAX = 0.2;
const TOP_DATA_LABEL_KEY = "__isTopDataLabel";
const BUBBLE_RADIUS_MIN = 5;
const BUBBLE_RADIUS_MAX = 40;

export default class BubbleChart extends BaseChart {
  min: MinMax = minMaxDefault();
  max: MinMax = minMaxDefault();
  bubbleSeries!: BubbleSeries[];
  isScatterPlot!: boolean;
  labelField!: "decompositionMetric" | "xAxis" | "yAxis";
  private labelsIndicator?: Label;
  private labelMetricKey?: string;
  private dataLabelsTopLimit?: number;

  initChart() {
    this.isScatterPlot = this.chartName === "scatterPlot";

    this.destroyLabelsIndicator();
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    this.createChart();

    this.createAxes();
    this.bubbleSeries = this.bubbleSeries.map((el, index) => {
      return { name: el.name, data: this.createSeries(el.name, index) };
    });

    this.showLegend(map(this.bubbleSeries, "name"), (this.chart as XYChart).series.values as LegendData, false);

    this.createCursor();
    this.renderLabelsIndicator();
  }

  protected mapData(data: ChartDataRecord[]): ChartDataRecord[] {
    this.bubbleSeries = [];
    const mappedData: ChartDataRecord[] = [];
    this.min = minMaxDefault();
    this.max = minMaxDefault();

    const defaultLabelField = this.isScatterPlot ? "yAxis" : "decompositionMetric";
    this.labelField = (this.settings.dataLabelField as typeof this.labelField) ?? defaultLabelField;
    this.labelMetricKey = this.resolveLabelMetricKey();
    this.dataLabelsTopLimit = this.getDataLabelsTopLimit();
    const labelRanking: Array<{ value: number; index: number }> = [];
    let preparedData: ChartDataRecord[] = cloneDeep(data);

    if (
      (this.module === PLAN_MODULE || this.isDualMode) &&
      (!this.settings?.useExploreData || this.isDualMode) &&
      this.settings?.showItems &&
      Number.isFinite(this.settings?.showItems)
    ) {
      const decompositionMetric = this.settings.decompositionMetric;
      const displayMetric = this.settings.displayMetric;

      preparedData = this.prepareTopPlanModuleData(
        preparedData,
        decompositionMetric,
        displayMetric,
        this.settings.showItems
      );
    }

    preparedData = preparedData.map((x) =>
      x[this.settings.displayMetric] === null ? { ...x, [this.settings.displayMetric]: 0 } : x
    );
    preparedData = orderBy(preparedData, [this.settings.displayMetric], ["desc"]) as ChartDataRecord[];

    preparedData = this.showItems(preparedData, this.settings.decompositionMetric, this.settings.displayMetric);

    preparedData.forEach((item) => {
      const x: number = item[this.settings.xAxis] === null ? 0 : (item[this.settings.xAxis] as number);
      const y: number = item[this.settings.yAxis] === null ? 0 : (item[this.settings.yAxis] as number);
      const value: number = item[this.settings.displayMetric] as number;
      const minMaxValues: MinMax = { xAxis: x, yAxis: y, displayMetric: value };

      Object.keys(minMaxDefault()).forEach((key) => {
        const value = minMaxValues[key] as number;

        if (this.max[key] === undefined || value > (this.max[key] as number)) {
          this.max[key] = value;
        }

        if (this.min[key] === undefined || value < (this.min[key] as number)) {
          this.min[key] = value;
        }
      });

      const seriesName: string = (item[this.settings.decompositionMetric] =
        (item[this.settings.decompositionMetric] as string) ?? Lang.null);

      this.bubbleSeries.push({ name: seriesName });
      const mappedPoint = omitBy({ x, y, value, name: seriesName }, isNil);
      mappedData.push(mappedPoint);

      if (this.dataLabelsTopLimit) {
        const labelValue = this.resolveLabelValueForRanking(item, { x, y, value });
        if (typeof labelValue === "number" && !Number.isNaN(labelValue)) {
          labelRanking.push({ value: labelValue, index: mappedData.length - 1 });
        }
      }
    });

    if (this.dataLabelsTopLimit && labelRanking.length) {
      const topItems = orderBy(labelRanking, ["value"], ["desc"]).slice(0, this.dataLabelsTopLimit);
      const allowedIndices = new Set(topItems.map((item) => item.index));
      mappedData.forEach((point, idx) => {
        if (allowedIndices.has(idx)) {
          (point as Record<string, unknown>)[TOP_DATA_LABEL_KEY] = true;
        }
      });
    }

    this.renderLabelsIndicator();

    return mappedData;
  }

  protected createSeries(name: string, index: number) {
    const seriesSettings: ILineSeriesSettings = {
      name,
      calculateAggregates: true,
      xAxis: this.axes.x,
      yAxis: this.axes.y,
      valueXField: "x",
      valueYField: "y",
      fill: this.getSeriesColor(index),
    };

    if (!this.isScatterPlot) {
      seriesSettings.valueField = "value";
    }

    const oneSeries = (this.chart as XYChart).series.push(LineSeries.new(this.root, seriesSettings));

    const circleTemplate = Template.new({}) as Template<Circle>;
    const circleSettings: ICircleSettings = {
      fill: oneSeries.get("fill"),
    };

    if (this.isScatterPlot) {
      circleSettings.radius = DOT_SIZE_STEP * this.settings.dotSize;
    }

    const sizeMin = Number(this.min["displayMetric"] ?? 0);
    const sizeMax = Number(this.max["displayMetric"] ?? 0);

    oneSeries.bullets.push((root, series, dataItem) => {
      const settings: ICircleSettings = { ...circleSettings };
      if (!this.isScatterPlot && this.isDualMode) {
        const ctx = (dataItem?.dataContext ?? {}) as Record<string, unknown>;
        settings.radius = mapToRange(Number(ctx.value ?? 0), sizeMin, sizeMax, BUBBLE_RADIUS_MIN, BUBBLE_RADIUS_MAX);
      }
      const graphics = Circle.new(this.root, settings, circleTemplate);
      return Bullet.new(this.root, {
        sprite: graphics,
      });
    });

    if (this.settings.showDataLabels) {
      const getFormatter = () => {
        if (this.labelField === "decompositionMetric") {
          const metricKey = this.labelMetricKey ?? this.settings.displayMetric;
          return (
            (metricKey ? this.formatters?.[metricKey] : undefined) ??
            this.formatters?.[this.settings.decompositionMetric]
          );
        }

        if (this.labelField === "xAxis") {
          return this.formatters?.[this.settings.xAxis];
        }

        if (this.labelField === "yAxis") {
          return this.formatters?.[this.settings.yAxis];
        }

        return this.labelMetricKey ? this.formatters?.[this.labelMetricKey] : undefined;
      };

      const metricFormatter = getFormatter();
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

      oneSeries.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerX: p50,
          dy: 5,
          ...chartFontSettings.label,
          fontSize: labelFontSize,
          layer: 1000,
        });

        const resolveValue = () => {
          const context = dataItem?.dataContext as Record<string, unknown> | undefined;
          const getFormatterForKey = (key?: string) => (key ? this.formatters?.[key] : undefined);

          if (this.labelField === "decompositionMetric") {
            const metricKey = this.labelMetricKey ?? this.settings.displayMetric;
            const direct = dataItem?.get("value");
            if (typeof direct === "number") {
              return { value: direct, formatter: getFormatterForKey(metricKey) };
            }

            const contextValue = context?.value;
            if (typeof contextValue === "number") {
              return { value: contextValue, formatter: getFormatterForKey(metricKey) };
            }

            const name = typeof context?.name === "string" ? (context.name as string) : undefined;
            if (name) {
              return { value: name, formatter: getFormatterForKey(this.settings.decompositionMetric) };
            }

            return { value: undefined };
          }

          if (this.labelField === "xAxis") {
            const key = this.settings.xAxis;
            const direct = dataItem?.get("valueX");
            if (typeof direct === "number") {
              return { value: direct, formatter: getFormatterForKey(key) };
            }

            const fallback = context?.x;
            return {
              value: typeof fallback === "number" ? fallback : undefined,
              formatter: getFormatterForKey(key),
            };
          }

          const key = this.settings.yAxis;
          const direct = dataItem?.get("valueY");
          if (typeof direct === "number") {
            return { value: direct, formatter: getFormatterForKey(key) };
          }

          const fallback = context?.y;
          return {
            value: typeof fallback === "number" ? fallback : undefined,
            formatter: getFormatterForKey(key),
          };
        };

        label.adapters.add("text", () => {
          const { value, formatter } = resolveValue();
          if (value === undefined || value === null || value === "") {
            return "";
          }

          const effectiveFormatter =
            typeof formatter === "function"
              ? formatter
              : typeof metricFormatter === "function"
              ? metricFormatter
              : undefined;

          if (typeof effectiveFormatter === "function") {
            return effectiveFormatter(value, formatterConfig);
          }

          if (typeof value === "number") {
            return this.root.numberFormatter.format(value, formatterConfig);
          }

          return `${value}`;
        });

        label.adapters.add("visible", () => {
          const { value } = resolveValue();
          if (value === undefined || value === null || value === "") {
            return false;
          }

          if (this.dataLabelsTopLimit) {
            const context = dataItem?.dataContext as Record<string, unknown> | undefined;
            if (!context || context[TOP_DATA_LABEL_KEY] !== true) {
              return false;
            }
          }

          if (typeof value === "number" && !this.isDualMode) {
            return value !== 0;
          }

          return true;
        });

        return Bullet.new(this.root, {
          sprite: label,
        });
      });
    }

    if (!this.isScatterPlot && !this.isDualMode) {
      oneSeries.set("heatRules", [
        {
          target: circleTemplate,
          min: BUBBLE_RADIUS_MIN,
          max: BUBBLE_RADIUS_MAX,
          minValue: this.min["displayMetric"],
          maxValue: this.max["displayMetric"],
          dataField: "value",
          key: "radius",
        },
      ]);
    }

    oneSeries.strokes.template.set("strokeOpacity", 0);

    this.createTooltip(oneSeries as TooltipSeries, () => {
      const tooltipValues = oneSeries.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;

      return tooltipValues
        ? [
            { key: this.settings.decompositionMetric, value: oneSeries.get("name") as string },
            { key: this.settings.displayMetric, value: tooltipValues?.value as number },
            { key: this.settings.xAxis, value: tooltipValues?.[oneSeries.get("valueXField") || ""] as number },
            { key: this.settings.yAxis, value: tooltipValues?.[oneSeries.get("valueYField") || ""] as number },
          ].filter((item) => !isNil(item.key))
        : [];
    });
    const currentCategory = this.data.filter((item: ChartDataRecord) => item["name"] === name);
    oneSeries.data.setAll(currentCategory);

    return oneSeries;
  }

  protected createChart() {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        panX: true,
        panY: true,
        layout: this.layout,
        ...chartInitialSettings,
      })
    );
  }

  protected createAxes() {
    const xRenderer = AxisRendererX.new(this.root, {
      minGridDistance: 60,
    });
    xRenderer.labels.template.setAll({
      ...chartFontSettings.label,
      minPosition: 0.01,
    });

    const x = (this.chart as XYChart).xAxes.push(
      ValueAxis.new(this.root, {
        renderer: xRenderer,
        extraMin: EXTRA_X_SPACE,
        extraMax: EXTRA_X_SPACE,
        strictMinMaxSelection: true,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.xAxis, this.data),
      })
    );

    const yRenderer = AxisRendererY.new(this.root, {});
    yRenderer.labels.template.setAll({
      ...chartFontSettings.label,
      minPosition: 0.1,
    });
    const y = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: yRenderer,
        extraMin: EXTRA_Y_MIN,
        extraMax: EXTRA_Y_MAX,
        strictMinMaxSelection: true,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxis, this.data),
      })
    );
    if (this.settings.xAxisTitle) {
      x.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
          ...chartFontSettings.axisTitle,
        })
      );
    }

    if (this.settings.yAxisTitle) {
      y.children.push(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          x: percent(-50),
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { x, y };
  }

  protected createCursor() {
    (this.chart as XYChart).set(
      "cursor",
      XYCursor.new(this.root, {
        xAxis: this.axes.x,
        yAxis: this.axes.y,
        snapToSeries: map(this.bubbleSeries, "data") as XYSeries[],
      })
    );
  }

  async refreshData(widget: ContentWidget) {
    await super.refreshData(widget);
    this.renderLabelsIndicator();
  }

  public renderLabelsIndicator(): void {
    // Always dispose before redrawing to survive re-renders / toggles.
    this.destroyLabelsIndicator();
    if (!this.chart || !this.settings.showDataLabels || !this.settings.showDataLabelsLegend) return;

    const metricTitle = this.resolveLabelMetricTitle();
    if (!metricTitle) return;

    const draw = () => {
      // double-check current setting in case it was toggled while queued
      if (!this.settings.showDataLabels) return;

      const label = Label.new(this.root, {
        text: `Labels: ${metricTitle}`,
        fontSize: 14,
        fontStyle: "italic",
        fill: color("#fff"),
        x: percent(0),
        y: percent(100),
        centerX: 0,
        centerY: percent(100),
        populateText: true,
      });

      this.root.container.children.push(label);
      this.labelsIndicator = label;
      (this.chart as XYChart).set("paddingBottom", LABELS_INDICATOR_PADDING);
    };

    // Queue after the current frame so container is fully laid out.
    this.root.events.once("frameended", draw);
  }

  private destroyLabelsIndicator(): void {
    if (this.labelsIndicator) {
      this.labelsIndicator.dispose();
      this.labelsIndicator = undefined;
    }
    if (this.chart) {
      (this.chart as XYChart).set("paddingBottom", 0);
    }
  }

  private resolveLabelMetricTitle(): string {
    // Use the actual metric key that drives label values (for decomposition -> displayMetric)
    const key =
      this.labelMetricKey ??
      (typeof this.labelField === "string" ? (this.settings[this.labelField] as string) : undefined) ??
      this.settings.displayMetric ??
      this.settings.dataLabelField;
    const dictionary = this.inputsDictionary?.displayMetrics ?? [];
    const formatted = dictionary.find((item) => item.value === key);
    if (formatted?.title) return formatted.title;
    if (typeof key === "string" && key.length) return key;
    return "";
  }

  private resolveLabelValueForRanking(
    original: ChartDataRecord,
    point: { x?: number; y?: number; value?: number }
  ): number | undefined {
    if (this.labelField === "xAxis") {
      const xKey = typeof this.settings.xAxis === "string" ? this.settings.xAxis : undefined;
      const xValue = point.x ?? (xKey ? (original[xKey] as number) : undefined);
      return typeof xValue === "number" ? xValue : undefined;
    }

    if (this.labelField === "yAxis") {
      const yKey = typeof this.settings.yAxis === "string" ? this.settings.yAxis : undefined;
      const yValue = point.y ?? (yKey ? (original[yKey] as number) : undefined);
      return typeof yValue === "number" ? yValue : undefined;
    }

    if (typeof point.value === "number") {
      return point.value;
    }

    if (this.labelMetricKey) {
      const metricKey = typeof this.labelMetricKey === "string" ? this.labelMetricKey : undefined;
      const metricValue = metricKey ? original[metricKey] : undefined;
      return typeof metricValue === "number" ? (metricValue as number) : undefined;
    }

    return undefined;
  }

  private resolveLabelMetricKey(): string | undefined {
    if (this.labelField === "xAxis") {
      return this.settings.xAxis;
    }

    if (this.labelField === "yAxis") {
      return this.settings.yAxis;
    }

    if (this.settings.displayMetric) {
      return this.settings.displayMetric;
    }

    return this.isScatterPlot ? this.settings.yAxis : undefined;
  }
}
